import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
} from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import MapEmbed from "../components/MapEmbed";
import { SERVICES, STORES, getStore, mapsLink, waLink } from "../data/siteData";

export default function StorePage({ id }) {
  const store = getStore(id);
  const others = STORES.filter((s) => s.id !== id);
  const services = store.services
    ? SERVICES.filter((s) => store.services.includes(s.id))
    : SERVICES;

  return (
    <>
      <SEO
        title={`${store.name} | Orari, Servizi e Contatti`}
        description={`${store.name} — ${store.fullAddress}. ${store.hoursSummary}. Scopri servizi, contatti e indicazioni stradali.`}
      />

      <section data-testid={`store-page-${store.id}`} className="relative flex min-h-[52svh] items-end overflow-hidden bg-ink">
        <img
          src={store.image}
          alt={`${store.name} — ${store.city}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: store.imagePosition || "center" }}
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/45 to-slate-950/20" />
        <div className="container-x relative z-10 pb-12 pt-28">
          <Link
            to="/#sedi"
            data-testid="store-back-link"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-300 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Tutte le sedi
          </Link>
          <Reveal>
            <p className="overline-tag mt-6 !text-emerald-300">{store.city}</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              {store.name}
            </h1>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <Clock className="h-3.5 w-3.5" />
                {store.hoursSummary}
              </span>
              {store.open247 && (
                <span className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                  Aperto 24/7
                </span>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-x grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Reveal>
              <p className="overline-tag">La sede</p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                {store.description}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-ink">
                Servizi disponibili in questa sede
              </h2>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {services.map((s) => (
                  <span
                    key={s.id}
                    data-testid={`store-service-${store.id}-${s.id}`}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-brand-dark"
                  >
                    {s.title}
                  </span>
                ))}
              </div>
            </Reveal>

            {store.gallery.length > 0 && (
              <div className="mt-12 grid gap-4 sm:grid-cols-2">
                {store.gallery.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`${store.name} — sala ${i + 1}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-2xl object-cover"
                  />
                ))}
              </div>
            )}

            <Reveal delay={0.15}>
              <h2 className="mt-12 font-display text-xl font-bold uppercase tracking-tight text-ink">
                Dove siamo
              </h2>
              <div className="mt-5">
                <MapEmbed query={store.mapQuery} label={store.id} />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="card-soft sticky top-28 p-6 sm:p-8">
                <h2 className="font-display text-lg font-bold uppercase tracking-tight text-ink">
                  Informazioni
                </h2>

                <div className="mt-6 space-y-5 text-sm">
                  <div className="flex gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                    <div>
                      <p className="font-semibold text-ink">Indirizzo</p>
                      <p className="mt-0.5 text-slate-500">{store.fullAddress}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                    <div>
                      <p className="font-semibold text-ink">Orari di apertura</p>
                      {store.hours.map((h) => (
                        <p key={h.label} className="mt-0.5 text-slate-500">
                          {h.label}: <span className="font-semibold text-slate-700">{h.value}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                    <div>
                      <p className="font-semibold text-ink">Telefono</p>
                      <p className="mt-0.5 text-slate-500">{store.phoneDisplay}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                    <div>
                      <p className="font-semibold text-ink">WhatsApp</p>
                      <p className="mt-0.5 text-slate-500">
                        {store.whatsappDisplay || "Prossimamente disponibile"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 space-y-2.5">
                  <a
                    href={mapsLink(store.fullAddress)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`directions-btn-${store.id}`}
                    className="btn btn-primary btn-md w-full"
                  >
                    <Navigation className="h-4 w-4" />
                    INDICAZIONI STRADALI
                  </a>
                  <a href={`tel:${store.phoneTel}`} data-testid={`call-btn-${store.id}`} className="btn btn-outline btn-md w-full">
                    <Phone className="h-4 w-4" />
                    CHIAMA ORA
                  </a>
                  {store.whatsapp && (
                    <a
                      href={waLink(store.whatsapp, `Ciao, vi contatto per la sede di ${store.shortName}.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`whatsapp-btn-${store.id}`}
                      className="btn btn-outline btn-md w-full"
                    >
                      <MessageCircle className="h-4 w-4" />
                      SCRIVICI SU WHATSAPP
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50 py-16">
        <div className="container-x">
          <Reveal>
            <h2 className="font-display text-xl font-bold uppercase tracking-tight text-ink">
              Scopri le altre sedi
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {others.map((s) => (
                <Link
                  key={s.id}
                  to={s.slug}
                  data-testid={`other-store-${s.id}`}
                  className="group card-soft flex items-center justify-between gap-4 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-10px_rgba(0,168,89,0.18)]"
                >
                  <span>
                    <span className="font-display text-base font-bold uppercase text-ink">{s.name}</span>
                    <span className="mt-1 block text-sm text-slate-500">{s.address}, {s.city}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 text-brand transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
